<i18n>
{
  "fr": {
    "configurator": {
      "loading": "Chargement du configurateur en cours, merci de patienter…",
      "orientationLandscapeDisabled": "Pour une expérience optimale, merci d'utiliser votre appareil en mode portait",
      "homeBtn": {
        "title": "Retourner à l'accueil"
      }
    }
  }
}
</i18n>

<template>
  <div class="configurator">
    <!-- Loading -->
    <div v-if="isLoading" class="configurator__loader-container-wrapper">
      <div class="configurator__loader-container">
        <LgfSpinner class="configurator__spinner" />

        <p>
          {{ $t('configurator.loading') }}
        </p>
      </div>
    </div>

    <template v-else>
      <!-- Landscape mode -->
      <div class="configurator__orientation-message">
        <div class="configurator__orientation-message-container">
          <p>
            {{ $t('configurator.orientationLandscapeDisabled') }}
          </p>
        </div>
      </div>

      <ConfiguratorLayout class="configurator__layout">
        <template #header>
          <ConfiguratorHeader>
            <template #left>
              <ConfiguratorHeaderBackButton
                :href="backButtonUrl"
                class="configurator__header-back-btn"
                @click.native="confirmExit"
              />
            </template>

            <template #center></template>

            <template #right>
              <ZendeskButton class="configurator__header-zendesk-btn" />
            </template>
          </ConfiguratorHeader>
        </template>

        <template #sidebar>
          <ConfiguratorEditor />
        </template>

        <template #content>
          <ConfiguratorPreview
            class="configurator__preview"
            :is-content-layout-trigger-visible="isContentLayoutTriggerVisible"
          />

          <PortalTarget name="portal-container--configurator-content" />
        </template>

        <template #footer>
          <ConfiguratorFooter />
        </template>
      </ConfiguratorLayout>
    </template>

    <ConfiguratorTracking />
  </div>
</template>

<script>
import ConfiguratorFooter from './ConfiguratorFooter';
import {
  GlassesAction,
  prefixType as prefixGlassType,
} from '../../store/modules/configurator/glasses';
import {
  ConfiguratorAction,
  prefixType as prefixConfiguratorType,
  mapState as mapConfiguratorState,
} from '../../store/modules/configurator';
import LgfSpinner from '../LgfSpinner';
import {
  MiscMutation,
  prefixType as prefixMiscType,
} from '../../store/modules/configurator/misc';
import ConfiguratorHeader from './ConfiguratorHeader';
import ConfiguratorHeaderBackButton from './ConfiguratorHeaderBackButton';
import ConfiguratorLayout from './ConfiguratorLayout';
import { Breakpoint } from '../../utils/breakpoints';
import {
  LayoutAction,
  LayoutMutation,
  LayoutStep,
  LayoutView,
  prefixType as prefixLayoutType,
  SESSION_STORAGE_TOOLTIP_CLICK_STATE_KEY,
} from '../../store/modules/configurator/layout';
import ConfiguratorEditor from './ConfiguratorEditor';
import ConfiguratorPreview from './ConfiguratorPreview';
import ConfiguratorTracking from './ConfiguratorTracking.vue';
import ZendeskButton from './ZendeskButton.vue';

export default {
  name: 'Configurator',
  components: {
    ConfiguratorTracking,
    ConfiguratorPreview,
    ConfiguratorHeaderBackButton,
    ConfiguratorLayout,
    ConfiguratorEditor,
    ConfiguratorHeader,
    LgfSpinner,
    ConfiguratorFooter,
    ZendeskButton,
  },
  props: {
    accountUrl: {
      type: String,
      required: true,
    },
    authUrl: {
      type: String,
      required: true,
    },
    displayDrafts: Boolean,
    isAuthenticated: Boolean,
    createCustomOrderUrl: {
      type: String,
      default: undefined,
    },
    cartUrl: {
      type: String,
      default: undefined,
    },
    editOrderItemUrl: {
      type: String,
      default: undefined,
    },
    backButtonUrl: {
      type: String,
      default: undefined,
    },
    homeUrl: {
      type: String,
      default: undefined,
    },
    blankConfiguratorUrl: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      isLoading: true,
      Breakpoint,
    };
  },
  computed: {
    ...mapConfiguratorState({
      themeType: 'themeType',
      activeCategoryId: 'activeCategoryId',
      activeSizeId: 'activeSizeId',
      activeColorId: 'activeColorId',
      activeProductId: 'activeProductId',
    }),
    isStepperInHeader() {
      return this.$matchMedia[Breakpoint.CONFIGURATOR_L];
    },
    isContentLayoutTriggerVisible() {
      return (
        !this.$matchMedia[Breakpoint.CONFIGURATOR_L] &&
        this.$store.state.configurator.layout.activeView === LayoutView.SIDEBAR
      );
    },
  },
  watch: {
    activeProductId: {
      handler() {
        this.trackProductViewed();
      },
    },
  },
  created() {
    this.$store
      .dispatch(prefixGlassType(GlassesAction.INIT_STATE), this.displayDrafts)
      .then(() => {
        this.hydrateState();

        this.isLoading = false;
      });

    if (sessionStorage) {
      // Restore session storage state
      globalThis.addEventListener('storage', () => {
        this.hydrateSessionStorageState();
      });

      this.hydrateSessionStorageState();
    }
  },
  mounted() {
    this.trackProductViewed();
  },
  methods: {
    hydrateState() {
      const searchParams = new URLSearchParams(globalThis.location.search);

      const productId = searchParams.get('productId');

      if (productId) {
        this.$store.commit(
          prefixLayoutType(LayoutMutation.SET_ACTIVE_STEP),
          LayoutStep.STEP_CONTENT
        );

        this.$store.commit(
          prefixLayoutType(LayoutMutation.SET_ACTIVE_VIEW),
          LayoutView.CONTENT
        );
      }

      this.$store.commit(prefixMiscType(MiscMutation.SET_STATE), {
        createCustomOrderUrl: this.createCustomOrderUrl || null,
        cartUrl: this.cartUrl || null,
        editOrderItemUrl: this.editOrderItemUrl || undefined,
        debugPrintingArea: searchParams.get('debugPrintingArea') !== null,
        debugTexture: searchParams.get('debugTexture') !== null,
        blankConfiguratorUrl: this.blankConfiguratorUrl || null,
      });

      this.$store.dispatch(
        prefixConfiguratorType(ConfiguratorAction.SET_ACTIVE_STATE),
        {
          accountUrl: this.accountUrl,
          authUrl: this.authUrl,
          categoryId: searchParams.get('categoryId'),
          customerProjectId: searchParams.get('customerProjectId'),
          isAuthenticated: this.isAuthenticated,
          isCustomerProjectDirty: !!searchParams.get('isOutdated'),
          themeType: searchParams.get('themeType'),
          productId,
          colorId: searchParams.get('colorId'),
          sizeId: searchParams.get('sizeId'),
          quantityId: searchParams.get('quantityId'),
          customizations: JSON.parse(searchParams.get('customizations')),
          screenPrintingColorId: searchParams.get('screenPrintingColorId'),
          cartItemId: searchParams.get('cartItemId'),
          fallbackWhenNotFound: true,
          orderItemId: searchParams.get('orderItemId'),
        }
      );
    },
    hydrateSessionStorageState() {
      // Btn render tooltip state
      const storageTooltipClickState = JSON.parse(
        sessionStorage.getItem(SESSION_STORAGE_TOOLTIP_CLICK_STATE_KEY)
      );

      if (
        Boolean(storageTooltipClickState) !==
        this.$store.state.configurator.layout.hasClickedRenderBtnTooltip
      ) {
        this.$store.dispatch(
          prefixLayoutType(LayoutAction.SET_RENDER_BTN_TOOLTIP_CLICKED_STATE),
          storageTooltipClickState
        );
      }
    },
    confirmExit() {
      if (confirm(this.$t('confirmExit'))) {
        globalThis.location.href = this.backButtonUrl;
      }
    },
    trackProductViewed() {
      if (window.klaviyo) {
        const searchParams = new URLSearchParams(window.location.search);
        const item = {
          ProductID: this.activeProductId || +searchParams.get('productId'),
          CategoryId: this.activeCategoryId || searchParams.get('categoryId'),
          IsAuthenticated: this.isAuthenticated,
          ThemeType: this.themeType,
          ColorId: this.activeColorId,
          SizeId: this.activeSizeId,
          OriginPathName: window.location.pathname,
        };

        window.klaviyo.push(['track', 'Viewed Product', item]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator {
  $breakpoint-orientation: (min-device-aspect-ratio string.unquote('1/1'))
    (max-height 31.25em);
  $layout-selector: '.configurator-layout__content';
  flex: 1;
  display: flex;
  flex-direction: column;
  background: $color-gray-gallery-2;
  height: 100%;

  @at-root %configurator-centered-message-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  @at-root %configurator-centered-message {
    text-align: center;
    margin: 1em;
    max-width: $breakpoint-xxs;
  }

  @include breakpoint($breakpoint-l) {
    /* stylelint-disable unit-no-unknown */
    max-height: 100svh;
    height: 100svh;
    /* stylelint-enable unit-no-unknown */
  }

  &__loader-container-wrapper {
    @extend %configurator-centered-message-wrapper;
  }

  &__loader-container {
    @extend %configurator-centered-message;
  }

  &__spinner {
    $font-size: 4em;
    font-size: $font-size;
    margin-bottom: math.div(1em, parse-int($font-size));
  }

  &__orientation-message {
    @extend %configurator-centered-message-wrapper;
    display: none;

    @include breakpoint($breakpoint-orientation) {
      display: flex;
    }
  }

  &__orientation-message-container {
    @extend %configurator-centered-message;
  }

  &__body {
    flex: 1;
  }

  &__layout {
    height: 100%;

    @include breakpoint($breakpoint-orientation) {
      display: none;
    }
  }

  &__header-zendesk-btn {
    display: flex;

    @include breakpoint($breakpoint-configurator-l) {
      display: none;
    }
  }

  &__preview {
    height: 69%;
    min-height: 0;

    @include breakpoint($breakpoint-configurator-l) {
      height: unset;
      flex: 1;
    }
  }

  &__header-toggle-btn {
    flex: none;

    &--enter,
    &--leave-to {
      opacity: 0;
    }

    &--enter-active,
    &--leave-active {
      transition: opacity math.div($default-transition-duration, 2);
    }

    &--enter-active {
      transition-delay: $base-layout-content-transition-duration;
    }
  }

  &__content-toggle-btn {
    flex: none;

    #{$layout-selector}--enter &,
    #{$layout-selector}--leave-to & {
      transform: translate(50%, -100vh) scale(0.5);
    }

    #{$layout-selector}--enter-active &,
    #{$layout-selector}--leave-active & {
      transition-property: opacity, transform;
      transition-duration: $base-layout-content-transition-duration;
    }
  }

  &__stepper-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
  }

  &__sidebar {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    min-height: 0;
  }
}
</style>
